'use client';

import { useMemo, useState } from 'react';
import { createFormatters } from '@/new_components/company-detail/currencyFormatters';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type * as XLSXType from 'xlsx';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CompanySearchResult } from '@/lib/actions/company-search';
import { DEFAULT_EXPORT_COLUMNS, EXPORTABLE_COLUMNS, EXPORT_COUNT_PRESETS, EXPORT_MAX, SWISS_DEFAULT_EXPORT_COLUMNS, columnLabelKey } from './resultsTableColumns';

function exportColumnLabel(
  id: string,
  t: ReturnType<typeof useTranslation>['t'],
  country?: string,
): string {
  if (id === 'euId') return 'EU-ID';
  const col = EXPORTABLE_COLUMNS.find((c) => c.id === id);
  return col
    ? t(`search.table.${columnLabelKey(col.id, col.labelKey, country)}`)
    : id;
}

// Local-time timestamp for export filenames, e.g. 2026-06-01_14-30-22.
function fileTimestamp(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`;
}

export async function exportToExcel(
  rows: CompanySearchResult[],
  columnIds: string[],
  t: ReturnType<typeof useTranslation>['t'],
  financialYear?: number,
  country?: string,
) {
  const XLSX: typeof XLSXType = await import('xlsx');
  // Keep canonical column order regardless of the order ids were toggled in.
  const cols = EXPORTABLE_COLUMNS.filter((c) => columnIds.includes(c.id));
  const data = rows.map((r, i) => {
    const row: Record<string, string | number> = { '#': i + 1 };
    for (const c of cols)
      row[exportColumnLabel(c.id, t, country)] = c.value(r);
    return row;
  });

  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Companies');
  const yearSuffix = financialYear ? `_${financialYear}` : '';
  XLSX.writeFile(wb, `company_search${yearSuffix}_${fileTimestamp()}.xlsx`);
}

// Excel export dialog (Pro/Admin): pick which columns to include and how many
// results to export (preset counts, capped at EXPORT_MAX and the total found).
// The form state lives in ExportDialogBody, which only mounts while the dialog
// is open — so every open starts from fresh defaults without a reset effect.
export function ExportDialog({
  open,
  onOpenChange,
  total,
  totalLabel,
  t,
  isExporting,
  exportError,
  onExport,
  country,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  total: number;
  totalLabel: string;
  t: ReturnType<typeof useTranslation>['t'];
  isExporting: boolean;
  exportError: boolean;
  onExport: (columnIds: string[], count: number) => void;
  country?: string;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <ExportDialogBody
          total={total}
          totalLabel={totalLabel}
          t={t}
          isExporting={isExporting}
          exportError={exportError}
          onCancel={() => onOpenChange(false)}
          onExport={onExport}
          country={country}
        />
      </DialogContent>
    </Dialog>
  );
}

function ExportDialogBody({
  total,
  totalLabel,
  t,
  isExporting,
  exportError,
  onCancel,
  onExport,
  country,
}: {
  total: number;
  totalLabel: string;
  t: ReturnType<typeof useTranslation>['t'];
  isExporting: boolean;
  exportError: boolean;
  onCancel: () => void;
  onExport: (columnIds: string[], count: number) => void;
  country?: string;
}) {
  const { i18n } = useTranslation();
  const numberFormatter = useMemo(
    () => createFormatters(i18n.language).fullNumber,
    [i18n.language],
  );
  const [selectedColumns, setSelectedColumns] = useState<string[]>(
    country === 'CH' || country === 'AT' ? SWISS_DEFAULT_EXPORT_COLUMNS : DEFAULT_EXPORT_COLUMNS,
  );
  const [selectedCount, setSelectedCount] = useState<number>(
    total > 100 ? 1000 : 100,
  );

  const toggleColumn = (id: string, checked: boolean) => {
    setSelectedColumns((prev) =>
      checked ? [...prev, id] : prev.filter((c) => c !== id),
    );
  };

  // A preset is offered only if the previous (smaller) preset doesn't already
  // cover every result — so we never show 10,000 when there are 200 matches,
  // but always keep at least one option that exports everything available.
  const presetEnabled = (idx: number) =>
    total > (EXPORT_COUNT_PRESETS[idx - 1] ?? 0);
  const exportCount = Math.min(selectedCount, total, EXPORT_MAX);
  const canSubmit = selectedColumns.length > 0 && total > 0 && !isExporting;

  return (
    <>
        <DialogHeader>
          <DialogTitle>{t('search.exportDialog.title', 'Excel-Export')}</DialogTitle>
          <DialogDescription>
            {t(
              'search.exportDialog.description',
              'Wählen Sie die Spalten und die Anzahl der Ergebnisse für den Export.',
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              {t('search.columns', 'Spalten')}
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              {EXPORTABLE_COLUMNS.map((col) => {
                const checked = selectedColumns.includes(col.id);
                return (
                  <label
                    key={col.id}
                    className="flex items-center gap-2 text-sm cursor-pointer"
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(v) => toggleColumn(col.id, v === true)}
                    />
                    <span>{exportColumnLabel(col.id, t, country)}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              {t('search.exportDialog.countLabel', 'Anzahl der Ergebnisse')}
            </div>
            <div className="flex flex-wrap gap-2">
              {EXPORT_COUNT_PRESETS.map((preset, idx) => {
                const enabled = presetEnabled(idx);
                return (
                  <Button
                    key={preset}
                    type="button"
                    size="sm"
                    variant={selectedCount === preset ? 'primary' : 'outline'}
                    disabled={!enabled || isExporting}
                    onClick={() => setSelectedCount(preset)}
                  >
                    {numberFormatter.format(preset)}
                  </Button>
                );
              })}
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              {t('search.exportDialog.summary', {
                count: numberFormatter.format(exportCount),
                total: totalLabel,
                defaultValue:
                  '{{count}} von {{total}} Ergebnissen werden exportiert',
              })}
            </div>
          </div>

          {exportError && (
            <div className="text-sm text-destructive">
              {t(
                'search.exportDialog.error',
                'Export fehlgeschlagen. Bitte erneut versuchen.',
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isExporting}>
            {t('common.buttons.cancel', 'Abbrechen')}
          </Button>
          <Button
            onClick={() => onExport(selectedColumns, exportCount)}
            disabled={!canSubmit}
          >
            {isExporting && <Loader2 className="h-3 w-3 animate-spin" />}
            {isExporting
              ? t('search.exporting', 'Exportiere…')
              : t('search.exportExcel')}
          </Button>
        </DialogFooter>
    </>
  );
}
